import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import { Shield } from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { useIsAdmin } from "@/lib/useIsAdmin";
import { WattsLogo } from "@/components/WattsLogo";

export const Route = createFileRoute("/admin/setup-mfa")({
  component: SetupMfaPage,
});

function SetupMfaPage() {
  const navigate = useNavigate();
  const { isAdmin, loading } = useIsAdmin();
  const [qr, setQr] = useState<string | null>(null);
  const [secret, setSecret] = useState<string>("");
  const [factorId, setFactorId] = useState<string>("");
  const [code, setCode] = useState("");
  const [verifying, setVerifying] = useState(false);
  const enrolled = useRef(false);

  useEffect(() => {
    if (loading) return;
    if (!isAdmin) { navigate({ to: "/", replace: true }); return; }
    if (enrolled.current) return;
    enrolled.current = true;
    (async () => {
      // Clean any unverified existing factors
      const { data: list } = await supabase.auth.mfa.listFactors();
      const verified = (list?.totp ?? []).find((f) => f.status === "verified");
      if (verified) { navigate({ to: "/admin/verify-mfa", replace: true }); return; }
      for (const f of list?.totp ?? []) {
        if (f.status !== "verified") await supabase.auth.mfa.unenroll({ factorId: f.id });
      }
      const { data, error } = await supabase.auth.mfa.enroll({
        factorType: "totp",
        friendlyName: `Watts Admin ${Date.now()}`,
      });
      if (error || !data) { toast.error(error?.message ?? "Error al iniciar 2FA"); return; }
      setQr(data.totp.qr_code);
      setSecret(data.totp.secret);
      setFactorId(data.id);
    })();
  }, [loading, isAdmin, navigate]);

  const verify = async (e: React.FormEvent) => {
    e.preventDefault();
    if (code.length !== 6 || !factorId) return;
    setVerifying(true);
    const { error } = await supabase.auth.mfa.challengeAndVerify({ factorId, code });
    setVerifying(false);
    if (error) { toast.error(error.message); return; }
    toast.success("2FA activado");
    navigate({ to: "/admin/puertos", replace: true });
  };

  return (
    <div className="flex min-h-screen flex-col items-center bg-[#0f0f0f] px-6 py-10 fade-in">
      <WattsLogo size={40} />
      <div className="mt-6 flex items-center gap-2 rounded-full bg-[#F5C518]/15 px-3 py-1 text-[10px] font-bold uppercase tracking-wider text-[#F5C518]">
        <Shield className="h-3 w-3" /> Admin · 2FA
      </div>
      <h1 className="mt-5 max-w-sm text-center font-['Barlow_Condensed',sans-serif] text-3xl font-bold uppercase tracking-wide text-white">
        Configura tu autenticación de dos factores
      </h1>
      <p className="mt-2 max-w-sm text-center text-[13px] text-[#888]">
        Escanea este QR con Google Authenticator o Authy
      </p>

      <div className="mt-6 flex h-56 w-56 items-center justify-center rounded-2xl bg-white p-3">
        {qr ? (
          <img src={qr} alt="QR 2FA" className="h-full w-full" />
        ) : (
          <div className="text-xs text-black">Cargando…</div>
        )}
      </div>

      {secret && (
        <p className="mt-3 break-all text-center text-[10px] text-[#666]">
          O introduce manualmente: <span className="font-mono text-[#aaa]">{secret}</span>
        </p>
      )}

      <form onSubmit={verify} className="mt-6 w-full max-w-xs space-y-3">
        <input
          inputMode="numeric"
          pattern="[0-9]*"
          maxLength={6}
          autoFocus
          value={code}
          onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
          placeholder="000000"
          className="h-14 w-full rounded-xl border border-[#2a2a2a] bg-[#1a1a1a] text-center font-mono text-2xl font-bold tracking-[0.5em] text-[#F5C518] outline-none focus:border-[#F5C518] focus:ring-2 focus:ring-[#F5C518]/30"
        />
        <button
          type="submit"
          disabled={verifying || code.length !== 6}
          className="press-scale h-12 w-full rounded-xl bg-[#F5C518] font-bold text-black disabled:opacity-50"
        >
          {verifying ? "Verificando…" : "Confirmar y activar"}
        </button>
      </form>
    </div>
  );
}
